import { useCallback, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import gsap from 'gsap';

type Variant = 'modal' | 'drawer';

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const hiddenState = (v: Variant): gsap.TweenVars => (v === 'drawer' ? { xPercent: 100 } : { opacity: 0, scale: 0.975, y: 8 });

// Enter on mount, animated exit before the parent unmounts the overlay. Scroll lock stays in useOverlay.
export default function useOverlayMotion(variant: Variant, onClose: () => void, panelRef: RefObject<HTMLDivElement | null>) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const closing = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useLayoutEffect(() => {
    if (reduced()) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.35, ease: 'power2.out' });
      gsap.fromTo(panelRef.current, hiddenState(variant), {
        opacity: 1, scale: 1, y: 0, xPercent: 0,
        duration: variant === 'drawer' ? 0.42 : 0.38,
        ease: variant === 'drawer' ? 'power3.out' : 'power2.out',
        clearProps: 'transform,opacity',
      });
    });
    return () => ctx.revert();
  }, [variant, panelRef]);

  const requestClose = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    if (reduced()) {
      closeRef.current();
      return;
    }
    gsap.to(backdropRef.current, { opacity: 0, duration: 0.3, ease: 'power2.inOut' });
    gsap.to(panelRef.current, { ...hiddenState(variant), duration: 0.32, ease: 'power2.inOut', onComplete: () => closeRef.current() });
  }, [variant, panelRef]);

  return { backdropRef, requestClose };
}